import { Injectable } from '@angular/core';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { ResourceTaskConfiguration, SchedulerTask, SchedulerTaskConfiguration } from '../../types/scheduler/scheduler-task.type';
import { BaseApiService } from './base-api.service';
import { SharePointDrive } from '../../types/share-point-drive.type';
import { RecoveryConfiguration } from '../../types/scheduler/recovery-configuration.type';
import { SharePointList } from '../../types/share-point-list.type';
import { DateHelper } from '../../helpers/date.helper';
import { BluePrismScheduleImportResult, EmailConnectionTestResult, EmailMailboxCheckModel } from '../../types/generated.types';

@Injectable({ providedIn: 'root' })
export class TasksApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/tasks`;
  }

  async getAllTasks(): Promise<SchedulerTask[]> {
    const tasks = await this.get<SchedulerTask[]>('');
    tasks.forEach(t => {
      ApiConversionHelper.convertDates(t, ['updated']);
      ApiConversionHelper.convertSchedulerTaskConfiguration(t.configuration);
      ApiConversionHelper.convertRecoveryConfiguration(t.recoveryConfiguration);
    });
    return tasks;
  }

  async getTasks(processId: string): Promise<SchedulerTask[]> {
    const tasks = await this.get<SchedulerTask[]>(`${processId}`);
    tasks.forEach(t => {
      ApiConversionHelper.convertDates(t, ['updated']);
      ApiConversionHelper.convertSchedulerTaskConfiguration(t.configuration);
      ApiConversionHelper.convertRecoveryConfiguration(t.recoveryConfiguration);
    });
    return tasks;
  }

  async getRunNowTask(processId: string): Promise<SchedulerTask> {
    const task = await this.get<SchedulerTask>(`${processId}/run-now`);
    ApiConversionHelper.convertSchedulerTaskConfiguration(task?.configuration);
    return task;
  }

  async getResourceTasks(resourceId: string): Promise<SchedulerTask> {
    return await this.get<SchedulerTask>(`resource-task/${resourceId}`);
  }

  async getHistoricalTask(taskId: string, timestamp: Date): Promise<SchedulerTask> {
    const task = await this.get<SchedulerTask>(`historical-task/${taskId}/${DateHelper.formatApiDateTime(timestamp)}`);
    if (task == null) {
      return null;
    }
    ApiConversionHelper.convertSchedulerTaskConfiguration(task.configuration);
    ApiConversionHelper.convertRecoveryConfiguration(task.recoveryConfiguration);
    return task;
  }

  async validateTaskCondition(conditionText: string): Promise<string> {
    return await this.postWithResult<{ conditionText: string }, string>('validate-condition', { conditionText });
  }

  async validateTaskAction(actionText: string): Promise<string> {
    return await this.postWithResult<{ actionText: string }, string>('validate-action', { actionText });
  }

  async validateTaskConfiguration(taskConfiguration: SchedulerTaskConfiguration): Promise<boolean> {
    const taskConfigurationCopy = structuredClone(taskConfiguration);
    ApiConversionHelper.convertSchedulerTaskConfigurationForApi(taskConfigurationCopy);
    return await this.postWithResult('validate-configuration', taskConfigurationCopy);
  }

  async getSharePointDrive(sharePointDriveUrl: string): Promise<SharePointDrive> {
    return await this.postWithResult<{ sharePointDriveUrl: string }, SharePointDrive>('get-share-point-drive', { sharePointDriveUrl }, null, true);
  }

  async getSharePointList(sharePointListUrl: string): Promise<SharePointList> {
    return await this.postWithResult<{ sharePointListUrl: string }, SharePointList>('get-share-point-list', { sharePointListUrl }, null, true);
  }

  async checkEmailMailbox(model: EmailMailboxCheckModel): Promise<EmailConnectionTestResult> {
    return await this.postWithResult<EmailMailboxCheckModel, EmailConnectionTestResult>('check-email-mailbox', model);
  }

  async createTask(taskConfiguration: SchedulerTaskConfiguration): Promise<void> {
    const taskConfigurationCopy = structuredClone(taskConfiguration);
    ApiConversionHelper.convertSchedulerTaskConfigurationForApi(taskConfigurationCopy);
    return await this.post<SchedulerTaskConfiguration>('', taskConfigurationCopy);
  }

  async updateTask(taskConfiguration: SchedulerTaskConfiguration): Promise<void> {
    const taskConfigurationCopy = structuredClone(taskConfiguration);
    ApiConversionHelper.convertSchedulerTaskConfigurationForApi(taskConfigurationCopy);
    return await this.put('', taskConfigurationCopy);
  }

  async createResourceTask(resourceTaskConfiguration: ResourceTaskConfiguration): Promise<void> {
    return await this.post<ResourceTaskConfiguration>('resource-task', resourceTaskConfiguration);
  }

  async updateResourceTask(resourceTaskConfiguration: ResourceTaskConfiguration): Promise<void> {
    return await this.put('resource-task', resourceTaskConfiguration);
  }

  async createRecoveryTask(recoveryConfiguration: RecoveryConfiguration): Promise<void> {
    const recoveryConfigurationCopy = structuredClone(recoveryConfiguration);
    ApiConversionHelper.convertTimeSpansForApi(recoveryConfigurationCopy, ['quarantineDuration']);
    return await this.post<RecoveryConfiguration>('recovery', recoveryConfigurationCopy);
  }

  async updateRecoveryTask(recoveryConfiguration: RecoveryConfiguration): Promise<void> {
    const recoveryConfigurationCopy = structuredClone(recoveryConfiguration);
    ApiConversionHelper.convertTimeSpansForApi(recoveryConfigurationCopy, ['quarantineDuration']);
    return await this.put('recovery', recoveryConfigurationCopy);
  }

  async deleteTask(taskId: string): Promise<void> {
    return await this.delete(`${taskId}`);
  }

  async importProcessesSettings(tenantId: string, file: File): Promise<string[]> {
    return await this.postWithResult<File, string[]>(`import/${tenantId}`, file);
  }

  downloadProcessesSettings(tenantId: string): void {
    this.download(`download/${tenantId}`);
  }

  async setTaskIsActive(taskId: string, isActive: boolean): Promise<SchedulerTask> {
    const task = await this.patch<boolean, SchedulerTask>(`${taskId}/is-active/`, isActive);
    ApiConversionHelper.convertSchedulerTaskConfiguration(task.configuration);
    ApiConversionHelper.convertRecoveryConfiguration(task.recoveryConfiguration);
    return task;
  }

  async reorderTasks(taskOrders: Record<string, number>): Promise<void> {
    return await this.patch('reorder', taskOrders);
  }

  async runTaskNow(taskId: string): Promise<void> {
    return await this.post(`${taskId}/run-now/`, null);
  }

  async cancelTaskNow(taskId: string): Promise<void> {
    return await this.post(`${taskId}/cancel-now/`, null);
  }

  async setSchedulerProcessIsActive(processId: string, isActive: boolean): Promise<boolean> {
    return await this.patch(`processes/${processId}/is-active/`, isActive);
  }

  async importTasksFromBluePrismSchedules(tenantId: string): Promise<BluePrismScheduleImportResult> {
    return await this.postWithResult<null, BluePrismScheduleImportResult>(`import-from-bp-schedules/${tenantId}`, null);
  }
}
